import {
  CommandOperationMap,
  Invariants,
  gateCommandByOperation
} from '@commandDrivenStateEngine/core/invariants';
import { SalesChannelSetupState } from './state';
import { SalesChannelSetupCommand } from './commands';

/**
 * Invariants for the sales-channel setup wizard (TV1-1904, rule-based rework).
 *
 * The privileged "start" triggers are reacted to by the submit policy, which
 * calls a backend command. They are capability-gated via `commandOperations` →
 * `gateCommandByOperation`, so the UI never surfaces a trigger the user lacks the
 * capability for. Inventory/logistics create automation rules (`SetRule`); billing
 * associates the entity (`UpdateSystemIntegrationConfiguration`); inline creation
 * provisions / adds entities.
 */

export const commandOperations: CommandOperationMap<
  SalesChannelSetupState,
  SalesChannelSetupCommand
> = {
  // Inventory + logistics persist channel-scoped automation rules.
  'salesChannelSetup/inventory/submit/start': 'SetRule',
  'salesChannelSetup/logistics/submit/start': 'SetRule',
  // Billing association + completion marker write to the channel.
  'salesChannelSetup/billing/submit/start': 'UpdateSystemIntegrationConfiguration',
  'salesChannelSetup/complete/start': 'UpdateSystemIntegrationConfiguration',
  // Inline creation reuses the canonical provisioning / billing commands.
  'salesChannelSetup/invoicing/provision/start': 'ProvisionSystemIntegration',
  'salesChannelSetup/billing/create/start': 'AddBillingEntity',
  'salesChannelSetup/logistics/register/start': 'ProvisionSystemIntegration'
};

const OPERATION_GATED_COMMANDS = new Set( Object.keys( commandOperations ));

/** API-source commands: policy outcomes + channel/catalog/rules reconciliation. */
const API_COMMANDS = new Set<SalesChannelSetupCommand['type']>([
  'salesChannelSetup/data/set',
  'salesChannelSetup/rules/sync',
  'salesChannelSetup/catalogs/setWarehouses',
  'salesChannelSetup/catalogs/setBillingEntities',
  'salesChannelSetup/catalogs/setLogistics',
  'salesChannelSetup/catalogs/setInvoicingIntegrations',
  'salesChannelSetup/catalogs/setOrderStatuses',
  'salesChannelSetup/inventory/submit/success',
  'salesChannelSetup/inventory/submit/error',
  'salesChannelSetup/billing/setFormErrors',
  'salesChannelSetup/billing/submit/success',
  'salesChannelSetup/billing/submit/error',
  'salesChannelSetup/billing/create/success',
  'salesChannelSetup/billing/create/error',
  'salesChannelSetup/invoicing/provision/success',
  'salesChannelSetup/invoicing/provision/error',
  'salesChannelSetup/logistics/submit/success',
  'salesChannelSetup/logistics/submit/error',
  'salesChannelSetup/logistics/register/success',
  'salesChannelSetup/logistics/register/error',
  'salesChannelSetup/complete/success',
  'salesChannelSetup/complete/error'
]);

/** Non-privileged user-source commands: drive the wizard inputs + UI lifecycle. */
const USER_UI_COMMANDS = new Set<SalesChannelSetupCommand['type']>([
  'salesChannelSetup/wizard/open',
  'salesChannelSetup/wizard/close',
  'salesChannelSetup/publications/setAutoImport',
  'salesChannelSetup/publications/setAutoImportOrders',
  'salesChannelSetup/inventory/setWarehouse',
  'salesChannelSetup/inventory/setTrigger',
  'salesChannelSetup/inventory/setStatus',
  'salesChannelSetup/billing/selectEntity',
  'salesChannelSetup/billing/setMode',
  'salesChannelSetup/billing/updateField',
  'salesChannelSetup/billing/setTrigger',
  'salesChannelSetup/billing/setStatus',
  'salesChannelSetup/invoicing/selectConnector',
  'salesChannelSetup/invoicing/updateCredentials',
  'salesChannelSetup/logistics/selectIntegration',
  'salesChannelSetup/logistics/setTrigger',
  'salesChannelSetup/logistics/setStatus',
  'salesChannelSetup/logistics/setMode',
  'salesChannelSetup/logistics/create/selectConnector',
  'salesChannelSetup/logistics/create/updateCredentials',
  'salesChannelSetup/logistics/create/setInstanceName'
]);

const canApply = ({
  state,
  command,
  meta
}: {
  state: SalesChannelSetupState;
  command: SalesChannelSetupCommand;
  meta: import( '@commandDrivenStateEngine/core/types' ).CommandMeta;
}): boolean => {
  const { source } = meta;
  const { type } = command;

  if ( source === 'api' ) {
    return API_COMMANDS.has( type );
  }

  if ( source === 'user' ) {
    if ( OPERATION_GATED_COMMANDS.has( type )) {
      return gateCommandByOperation( commandOperations, { state, command, meta });
    }
    return USER_UI_COMMANDS.has( type );
  }

  // AI / URL sources: a privileged per-channel setup flow — deny all.
  return false;
};

const normalize = ( state: SalesChannelSetupState ): SalesChannelSetupState => state;

const validate = ( _state: SalesChannelSetupState ): Record<string, string> | null => null;

export const invariants: Invariants<SalesChannelSetupState, SalesChannelSetupCommand> = {
  normalize,
  canApply,
  validate
};
